import { seesAllProjects, type StaffSubject } from './staff';

// Mirrors backend utils/projectAccess.ts: what a signed-in user may do in one
// project. The owner is project.owner; staff with the All Projects module act
// as 'admin'; members are editors, commenters or viewers (old 'admin' / 'owner'
// member entries read as editor).

export type ProjectRole = 'owner' | 'admin' | 'editor' | 'commenter' | 'viewer';
export type MemberRole = 'editor' | 'commenter' | 'viewer';

export const MEMBER_ROLES: { value: MemberRole; label: string; hint: string }[] = [
  { value: 'editor', label: 'Editor', hint: 'Uploads, deletes and changes files, notes and measurements; shares the project' },
  { value: 'commenter', label: 'Commenter', hint: 'Views and downloads; adds notes, replies and measurements' },
  { value: 'viewer', label: 'Viewer', hint: 'Views and downloads only' },
];

export const memberRoleLabel = (role: string | null | undefined): string =>
  MEMBER_ROLES.find((r) => r.value === role)?.label ?? (role === 'admin' || role === 'owner' ? 'Editor' : 'Viewer');

interface ProjectLike {
  owner?: { id: string } | null;
  members?: { user?: { id: string } | null; role: string }[] | null;
}

export function projectRoleFor(project: ProjectLike | null | undefined, user: (StaffSubject & { id: string }) | null | undefined): ProjectRole | null {
  if (!user) return null;
  if (seesAllProjects(user)) return 'admin';
  if (!project) return null;
  if (project.owner?.id === user.id) return 'owner';
  const role = project.members?.find((m) => m.user?.id === user.id)?.role;
  if (!role) return null;
  if (role === 'editor' || role === 'commenter' || role === 'viewer') return role;
  return role === 'admin' || role === 'owner' ? 'editor' : 'viewer';
}

// Files, folders, settings of the Scene, share links.
export const canEditProject = (role: ProjectRole | null): boolean =>
  role === 'owner' || role === 'admin' || role === 'editor';

// Notes, replies, note PDFs and measurements.
export const canCommentProject = (role: ProjectRole | null): boolean => canEditProject(role) || role === 'commenter';

// Members, project settings, the visitors log, anyone's notes.
export const managesProject = (role: ProjectRole | null): boolean => role === 'owner' || role === 'admin';
